import type { CommonColors } from '@mui/material/styles/createPalette';

import type { PaletteColorNoChannels } from './core/palette';
import type { ThemeDirection, ThemeColorScheme, ThemeCssVariables } from './types';

// ----------------------------------------------------------------------

type ThemeConfig = {
  classesPrefix: string;
  modeStorageKey: string;
  direction: ThemeDirection;
  defaultMode: ThemeColorScheme;
  cssVariables: ThemeCssVariables;
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error',
    PaletteColorNoChannels
  > & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: Record<
      '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
      string
    >;
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  direction: 'ltr',
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  classesPrefix: 'xpanse',
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'Public Sans Variable',
    secondary: 'Barlow',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#CAD0FF',
      light: '#8897FF',
      main: '#5166FE',
      dark: '#3D4ECD',
      darker: '#3742A4',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#EFD6FF',
      light: '#C684FF',
      main: '#8E33FF',
      dark: '#5119B7',
      darker: '#27097A',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#C7EEFF',
      light: '#6FD2FC',
      main: '#31BFFC',
      dark: '#006D9C',
      darker: '#014664',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#CDF9E1',
      light: '#7DF0B2',
      main: '#09C25E',
      dark: '#0D8745',
      darker: '#096131',
      contrastText: '#ffffff',
    },
    warning: {
      lighter: '#FCF0C6',
      light: '#FEDC65',
      main: '#F9C200',
      dark: '#A27D00',
      darker: '#654F00',
      contrastText: '#1C252E',
    },
    error: {
      lighter: '#FFD8DF',
      light: '#FFA0B1',
      main: '#FF2950',
      dark: '#B81331',
      darker: '#670618',
      contrastText: '#FFFFFF',
    },
    grey: {
      '50': '#FCFDFD',
      '100': '#F7F8FA',
      '200': '#ECEDF3',
      '300': '#B7BBD1',
      '400': '#5D6692',
      '500': '#919EAB',
      '600': '#29365F',
      '700': '#0B1F5A',
      '800': '#02174E',
      '900': '#000236',
    },
    common: { black: '#000000', white: '#FFFFFF' },
  },
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
};
